/**
 * useRoleSwitch Hook
 * 
 * Manages mock role state for viewing the application as a different role.
 * Stores mock role ID in localStorage and provides methods to set/clear it.
 *
 * Dev-only: in production builds, persisted mock roles are ignored and cleared so
 * /rbac/permissions/me remains the single source of truth (no accidental admin UI).
 */

import { useState, useEffect, useCallback } from 'react';

const MOCK_ROLE_STORAGE_KEY = 'tracekit-mock-role-id';

const isDevMockRoleEnabled = import.meta.env.DEV;

export interface UseRoleSwitchReturn {
  mockRoleId: string | null;
  setMockRole: (roleId: string | null) => void;
  clearMockRole: () => void;
  isMockRoleActive: boolean;
}

export function useRoleSwitch(): UseRoleSwitchReturn {
  const [mockRoleId, setMockRoleId] = useState<string | null>(null);

  // Load mock role from localStorage on mount (development only)
  useEffect(() => {
    try {
      if (!isDevMockRoleEnabled) {
        localStorage.removeItem(MOCK_ROLE_STORAGE_KEY);
        return;
      }
      const stored = localStorage.getItem(MOCK_ROLE_STORAGE_KEY);
      if (stored) {
        setMockRoleId(stored);
      }
    } catch (error) {
      console.warn('[useRoleSwitch] Failed to load mock role from localStorage:', error);
    }
  }, []);

  // Set mock role
  const setMockRole = useCallback((roleId: string | null) => {
    try {
      if (!isDevMockRoleEnabled && roleId) {
        return;
      }
      if (roleId) {
        localStorage.setItem(MOCK_ROLE_STORAGE_KEY, roleId);
        setMockRoleId(roleId);
      } else {
        localStorage.removeItem(MOCK_ROLE_STORAGE_KEY);
        setMockRoleId(null);
      }
    } catch (error) {
      console.warn('[useRoleSwitch] Failed to save mock role to localStorage:', error);
    }
  }, []);

  // Clear mock role
  const clearMockRole = useCallback(() => {
    setMockRole(null);
  }, [setMockRole]);

  // Check if mock role is active (never in production — avoids overriding real RBAC)
  const isMockRoleActive = isDevMockRoleEnabled && mockRoleId !== null;

  return {
    mockRoleId: isDevMockRoleEnabled ? mockRoleId : null,
    setMockRole,
    clearMockRole,
    isMockRoleActive,
  };
}

// Clear mock role on logout (call this from logout handler)
export function clearMockRoleOnLogout() {
  try {
    localStorage.removeItem(MOCK_ROLE_STORAGE_KEY);
  } catch (error) {
    console.warn('[useRoleSwitch] Failed to clear mock role on logout:', error);
  }
}


